"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Mail,
  CheckCircle2,
  ArrowLeft,
  Info,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getAuthConfig } from "@/app/utils/environmentUtils";
import {
  setToken,
  setUser as persistAuthUser,
  getToken,
} from "@/app/services/utils";
import { CLIENT_ROLE_ID } from "@/app/constants/roleConstants";
import { authService } from "@/app/services/authService";
import useUserStore from "@/store/user";
import { readJwtPayloadLoose } from "@/app/lib/auth/read-jwt-payload-client";
import {
  EMAIL_NOT_RECOGNIZED_CODE,
  EMAIL_NOT_RECOGNIZED_MESSAGE,
  STAFF_LOGIN_PATH,
  WRONG_PORTAL_STAFF_CODE,
  getStaffLoginPathWithEmail,
} from "@/app/lib/auth/login-routes";
import {
  formatWrongPortalAccountHeadline,
  WRONG_PORTAL_STAFF_BODY,
  WRONG_PORTAL_STAFF_BUTTON_LABEL,
} from "@/app/lib/auth/wrong-portal-account-label";

type FormStep = "email" | "sent" | "staffAccount";

export type ClientMagicLinkFormStep = FormStep;

type MagicLinkResponse = {
  error?: string;
  message?: string;
  code?: string;
  loginPath?: string;
  accountTypeLabel?: string;
};

function AgentStaffFooter() {
  return (
    <>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-stone-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-stone-100 px-2 text-stone-500">Agent or staff</span>
        </div>
      </div>

      <p className="text-center text-sm text-stone-600">
        Travel Agent, Lodge Manager, or Staff?{" "}
        <Link
          href={STAFF_LOGIN_PATH}
          className="font-medium text-[#2C5282] hover:text-[#1A365D] underline-offset-4 hover:underline"
        >
          Sign in with your password
        </Link>
      </p>
    </>
  );
}

export function ClientMagicLinkForm({
  className,
  onStepChange,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  onStepChange?: (step: FormStep) => void;
}) {
  const [step, setStepState] = React.useState<FormStep>("email");
  const [email, setEmail] = React.useState("");
  const [staffAccountTypeLabel, setStaffAccountTypeLabel] = React.useState("Staff");
  const [isLoading, setIsLoading] = React.useState(false);
  const [resumingSession, setResumingSession] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const router = useRouter();
  const { toast } = useToast();
  const { setUser: setStoreUser } = useUserStore();

  const setStep = React.useCallback(
    (next: FormStep) => {
      setStepState(next);
      onStepChange?.(next);
    },
    [onStepChange]
  );

  React.useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) return;

    const payload = readJwtPayloadLoose(token);
    const exp = payload?.exp;
    if (exp != null && typeof exp === "number" && exp * 1000 <= Date.now()) {
      return;
    }

    setResumingSession(true);
    setIsLoading(true);
    void (async () => {
      try {
        const user = await authService.getCurrentUser();
        if (cancelled || !user) return;
        if (user.role?.id === CLIENT_ROLE_ID) {
          setStoreUser(user);
          router.replace("/my/dashboard");
        }
      } finally {
        if (!cancelled) {
          setResumingSession(false);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [router, setStoreUser]);

  const applyMagicLinkError = (data: MagicLinkResponse): boolean => {
    if (data.code === WRONG_PORTAL_STAFF_CODE) {
      setStaffAccountTypeLabel(
        typeof data.accountTypeLabel === "string" && data.accountTypeLabel.trim()
          ? data.accountTypeLabel.trim()
          : "Staff"
      );
      setErrorMessage(null);
      setStep("staffAccount");
      return true;
    }

    if (data.code === EMAIL_NOT_RECOGNIZED_CODE) {
      setErrorMessage(EMAIL_NOT_RECOGNIZED_MESSAGE);
      setStep("email");
      return true;
    }

    return false;
  };

  const handleRequestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const authConfig = getAuthConfig();
      const baseUrl = authConfig.apiBaseUrl;

      const response = await fetch(
        `${baseUrl}/api/authentication/magic-link/request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        }
      );

      const data = (await response.json()) as MagicLinkResponse;

      if (!response.ok) {
        if (applyMagicLinkError(data)) {
          return;
        }
        const message = data.error || "Unable to send sign-in link.";
        setErrorMessage(message);
        toast({ title: "Error", description: message, variant: "destructive" });
        return;
      }

      setStep("sent");
      toast({
        title: "Check your email",
        description: data.message,
      });
    } catch {
      const message = "An unexpected error occurred. Please try again.";
      setErrorMessage(message);
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const authConfig = getAuthConfig();
      const baseUrl = authConfig.apiBaseUrl;
      const response = await fetch(
        `${baseUrl}/api/authentication/magic-link/request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        }
      );
      const data = (await response.json()) as MagicLinkResponse;
      if (!response.ok) {
        if (applyMagicLinkError(data)) {
          return;
        }
        toast({
          title: "Error",
          description: data.error || "Unable to resend link.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Link sent",
        description: data.message,
      });
    } catch {
      toast({
        title: "Error",
        description: "Unable to resend link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (resumingSession) {
    return (
      <div className={cn("flex items-center justify-center gap-2 py-8 text-sm text-stone-600", className)} {...props}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Restoring your session…
      </div>
    );
  }

  if (step === "staffAccount") {
    const continueHref = getStaffLoginPathWithEmail(email);

    return (
      <div
        className={cn(
          "flex w-full flex-col items-center justify-center gap-6",
          className
        )}
        {...props}
      >
        <div className="w-full rounded-lg border border-sky-200 bg-sky-50 p-6 text-center space-y-3">
          <Info className="mx-auto h-10 w-10 text-[#2C5282]" aria-hidden />
          <h3 className="text-lg font-semibold text-stone-900 break-words">
            {formatWrongPortalAccountHeadline(email, staffAccountTypeLabel)}
          </h3>
          <p className="text-sm leading-relaxed text-stone-600">
            {WRONG_PORTAL_STAFF_BODY}
          </p>
        </div>

        <Button
          asChild
          className="h-11 w-full shrink-0 rounded-md bg-[#2C5282] px-4 text-sm font-medium text-white hover:bg-[#1A365D] sm:h-12 sm:text-base [&_a]:flex [&_a]:h-full [&_a]:w-full [&_a]:items-center [&_a]:justify-center [&_a]:whitespace-nowrap [&_a]:text-center"
        >
          <Link href={continueHref}>{WRONG_PORTAL_STAFF_BUTTON_LABEL}</Link>
        </Button>
      </div>
    );
  }

  if (step === "sent") {
    return (
      <div className={cn("grid gap-6", className)} {...props}>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center space-y-3">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h3 className="text-lg font-semibold text-stone-900">Check your email</h3>
          <p className="text-sm text-stone-600">
            We sent a secure sign-in link to{" "}
            <span className="font-medium text-stone-800">{email}</span>.
            You can use this link whenever you need to sign in.
          </p>
        </div>

        <div className="space-y-3 text-center">
          <p className="text-sm text-stone-500">
            Didn&apos;t receive it? Check your spam folder or resend.
          </p>
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={handleResend}
            className="w-full border-stone-300"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resend sign-in link
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full text-stone-600"
            onClick={() => {
              setStep("email");
              setErrorMessage(null);
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Use a different email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={handleRequestLink}>
        <div className="grid gap-4">
          <div className="grid gap-1">
            <Label className="text-stone-700" htmlFor="client-email">
              Email address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                id="client-email"
                placeholder="name@example.com"
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
                className="pl-10 border-stone-300 focus:border-amber-500 focus:ring-amber-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <p className="text-xs text-stone-500 mt-1">
              We&apos;ll email you a secure link — no password needed.
            </p>
          </div>

          {errorMessage ? (
            <p className="text-sm text-red-600" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={isLoading}
            className="bg-[#2C5282] hover:bg-[#1A365D] text-white"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send sign-in link
          </Button>
        </div>
      </form>

      <AgentStaffFooter />
    </div>
  );
}
